import { useEffect, useRef, useState } from 'react';
import { CancellationSurveyModal } from './CancellationSurveyModal';
import { OfferModal } from './OfferModal';
import { OfferAcceptedModal } from './OfferAcceptedModal';
import { GoodbyeModal } from './GoodbyeModal';
import { useCancellationFlow } from '../../hooks/useCancellationFlow';
import { CANCELLATION_STEPS } from '../../constants/cancellationFlow';

interface CancellationFlowOrchestratorProps {
    isOpen: boolean;
    platformSubscriptionId: string | null;
    hasTakenFreeMonth?: boolean;
    shouldShowOffer: boolean;
    onComplete: () => void;
}

export const CancellationFlowOrchestrator = ({
    isOpen,
    platformSubscriptionId,
    hasTakenFreeMonth = false,
    shouldShowOffer,
    onComplete,
}: CancellationFlowOrchestratorProps) => {
    // Create a key that changes when flow reopens to force complete remount
    const [flowKey, setFlowKey] = useState(0);
    const wasOpenRef = useRef(false);

    // Capture initial values when flow opens to prevent mid-flow changes from affecting the flow
    const [initialValues, setInitialValues] = useState({
        hasTakenFreeMonth: false,
        shouldShowOffer: false,
    });

    useEffect(() => {
        // Only increment key when transitioning from closed to open (reopening)
        if (isOpen && !wasOpenRef.current) {
            setFlowKey(prev => prev + 1);
            // Capture the initial values when the flow opens
            setInitialValues({
                hasTakenFreeMonth,
                shouldShowOffer,
            });
        }
        wasOpenRef.current = isOpen;
    }, [isOpen, hasTakenFreeMonth, shouldShowOffer]);

    if (!isOpen) {
        return null;
    }

    return (
        <CancellationFlowContent
            key={flowKey}
            platformSubscriptionId={platformSubscriptionId}
            hasTakenFreeMonth={initialValues.hasTakenFreeMonth}
            shouldShowOffer={initialValues.shouldShowOffer}
            onComplete={onComplete}
        />
    );
};

// Separate component so the hook resets when key changes
const CancellationFlowContent = ({
    platformSubscriptionId,
    hasTakenFreeMonth,
    shouldShowOffer,
    onComplete,
}: Omit<CancellationFlowOrchestratorProps, 'isOpen'>) => {
    const {
        currentStep,
        offerType,
        isOfferLoading,
        isSurveyLoading,
        subscriptionEndDate,
        handleSurveyComplete,
        handleOfferAccept,
        handleOfferDecline,
        handleOfferAcceptedClose,
        handleGoodbyeEmojiClick,
        handleGoodbyeClose,
    } = useCancellationFlow({
        platformSubscriptionId,
        hasTakenFreeMonth,
        shouldShowOffer,
        onComplete,
    });

    return (
        <>
            {/* Step 1: Survey */}
            <CancellationSurveyModal
                isOpen={currentStep === CANCELLATION_STEPS.SURVEY}
                onClose={onComplete}
                onComplete={handleSurveyComplete}
                hasTakenFreeMonth={hasTakenFreeMonth}
                isLoading={isSurveyLoading}
            />

            {/* Step 2: Offer */}
            <OfferModal
                isOpen={currentStep === CANCELLATION_STEPS.OFFER}
                offerType={offerType}
                onAccept={handleOfferAccept}
                onDecline={handleOfferDecline}
                isLoading={isOfferLoading}
            />

            {/* Step 3: Offer Accepted Confirmation */}
            <OfferAcceptedModal
                isOpen={currentStep === CANCELLATION_STEPS.OFFER_ACCEPTED}
                offerType={offerType}
                onClose={handleOfferAcceptedClose}
            />

            {/* Step 4: Goodbye (with emoji survey) */}
            <GoodbyeModal
                isOpen={currentStep === CANCELLATION_STEPS.GOODBYE}
                onClose={handleGoodbyeClose}
                onEmojiClick={handleGoodbyeEmojiClick}
                validUntilDate={subscriptionEndDate}
            />
        </>
    );
};
